import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { ApiService, CrudService, InsuranceProviderLookup, PriceModel, ProductModel, ProgressIndicatorService } from '@mmx/shared'
import { Subject, takeUntil, tap } from 'rxjs'

import { ProductPriceDialogComponent } from '../product-price-dialog/product-price-dialog.component'

@Component({
  selector: 'mmx-product-prices',
  templateUrl: './product-prices.component.html',
  styleUrls: ['./product-prices.component.scss'],
  standalone: false,
})

export class ProductPricesComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator)
  public paginator: MatPaginator

  @Input()
  public product: ProductModel

  @Output()
  public updatePrices = new EventEmitter<PriceModel[]>()

  @Output()
  public openFeeSchedule = new EventEmitter<string>()

  public prices: MatTableDataSource<PriceModel>
  public carriers: InsuranceProviderLookup[] = []
  public productsDisplayedColumns = ['price', 'code', 'requirements', 'actions']

  private onDestroy$ = new Subject<void>()

  constructor(
    private progressService: ProgressIndicatorService,
    private restService: ApiService,
    private crudService: CrudService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.loadPrices()
    this.loadInsuranceProviderLookups()
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public getCarrierNames(carrierIds: string[]): string[] {
    return carrierIds.map((id: string) => {
      const carrier = this.carriers.find((c) => c.id === id)
      return carrier ? carrier.viewValue : id
    })
  }

  public addProductPrice(): void {
    this.dialog
      .open(ProductPriceDialogComponent, {
        panelClass: 'dialog-form',
        width: '500px',
        data: {
          product: new ProductModel({
            ...this.product.toJSON(),
          }),
        },
      })
      .afterClosed()
      .subscribe((price: PriceModel) => {
        if (price) {
          this.loadPrices()
        }
      })
  }

  public editProductPrice(price: PriceModel): void {
    this.dialog
      .open(ProductPriceDialogComponent, {
        panelClass: 'dialog-form',
        width: '400px',
        data: {
          product: new ProductModel({
            ...this.product.toJSON(),
          }),
          price,
        },
      })
      .afterClosed()
      .subscribe((updatePrice: PriceModel) => {
        if (updatePrice) {
          this.loadPrices()
        }
      })
  }

  public deleteProductPrice(price: PriceModel): void {
    const process = this.progressService.start()
    this.restService.deleteProductPrice(price).subscribe(
      () => {
        process.stop()
        this.loadPrices()
      },
      () => {
        process.stop('Failed to delete product price')
      },
    )
  }

  private loadInsuranceProviderLookups() {
    this.crudService.index(InsuranceProviderLookup).pipe(
      takeUntil(this.onDestroy$),
      tap((lookups) => this.carriers = lookups),
    ).subscribe()
  }

  private loadPrices() {
    const progress = this.progressService.start()
    if (!this.product?.id) {
      this.prices = new MatTableDataSource<PriceModel>([])
      this.prices.paginator = this.paginator
      progress.stop()
      return
    }

    this.restService
      .getProduct(this.product.id, { withPrices: true })
      .subscribe({
        next: (product: ProductModel) => {
          this.updatePrices.emit(product.prices)
          this.prices = new MatTableDataSource<PriceModel>(product.prices)
          this.prices.paginator = this.paginator
          progress.stop()
        },
        error: () => progress.stop('Failed to load product details'),
      })
  }
}
